/**
 * Staff runtime (runtime-handoff-v5, directive §4/§13).
 *
 * Instances the 256 staff from staff_placements.json using SHARED geometry/materials
 * (no per-person GLB). There is no dedicated staff mesh in the handoff, so staff
 * reuse the crowd static person archetype (they ARE people), instanced one draw per
 * part — optionally tinted per role.
 *
 * §4 fix: until V5.1 corrects them, the 16 staff detected inside the field of play
 * are SKIPPED (not rendered) with a warning — never silently moved at runtime. When
 * V5.1 lands, `insidePlayingArea` is [] and all 256 render.
 */
import { InstancedMesh, Matrix4, Object3D, StaticDrawUsage } from 'three';
import { composeStaffMatrix, type StaffParseResult, type StaffInstance } from './staffPlacements.js';
import type { CrowdArchetype } from './CrowdSystem.js';

export interface StaffBuildStats {
  rendered: number;
  skipped: number;
  skippedNames: string[];
  drawCalls: number;
}

export class StaffSystem {
  readonly root = new Object3D();
  private meshes: InstancedMesh[] = [];
  private stats: StaffBuildStats | null = null;

  constructor() { this.root.name = 'StaffSystem'; }

  /**
   * Build instanced staff from the parsed placements + a person archetype. Skips
   * any staff inside the field of play (§4) and warns.
   */
  build(parsed: StaffParseResult, archetype: CrowdArchetype, warn: (m: string) => void = (m) => console.warn(m)): StaffBuildStats {
    this.dispose();
    const skip = new Set(parsed.insidePlayingArea);
    const valid: StaffInstance[] = parsed.staff.filter((s) => !skip.has(s.name));

    if (skip.size > 0) {
      warn(`[Stadium] §13: skipping ${skip.size} staff inside the field of play (Agent 1 fixing in V5.1): ${[...skip].join(', ')}`);
    }

    const m = new Matrix4();
    for (const part of archetype.parts) {
      const inst = new InstancedMesh(part.geometry, part.material, valid.length);
      inst.name = `Staff_${archetype.label}`;
      inst.instanceMatrix.setUsage(StaticDrawUsage);
      valid.forEach((s, i) => { composeStaffMatrix(s, m); inst.setMatrixAt(i, m); });
      inst.instanceMatrix.needsUpdate = true;
      inst.frustumCulled = true;
      this.meshes.push(inst);
      this.root.add(inst);
    }

    this.stats = {
      rendered: valid.length,
      skipped: skip.size,
      skippedNames: [...skip],
      drawCalls: this.meshes.length,
    };
    return this.stats;
  }

  getStats(): StaffBuildStats | null { return this.stats; }
  setEnabled(on: boolean): void { this.root.visible = on; }

  dispose(): void {
    for (const mesh of this.meshes) { this.root.remove(mesh); mesh.dispose(); }
    this.meshes = [];
  }
}
